// -----------------------------------------------------------------------
// useApiFeedback — one shared hook that turns any axios / fetch error into
// a **visible** user-facing message. Solves the "silent save failure" bug
// pattern we hit on the used-car-inspection form: an error banner rendered
// far above the fold on 800-line forms was invisible on mobile, so users
// clicked Save at the bottom and thought "nothing happened".
//
// What it does:
//   • `error`      — string state you render in the form's local error banner
//   • `showError`  — sets `error`, fires a sonner toast (guaranteed visible),
//                    and scrolls the banner referenced by `bannerRef` into
//                    view so users on any screen size see the failure.
//   • `showSuccess`— convenience wrapper around toast.success for consistency
//   • `clearError` — hides the banner (e.g. on new save attempt)
//   • `bannerRef`  — attach to your <div ref={bannerRef}> error element
//
// The formatter unwraps Pydantic 422 responses (arrays of {loc,msg,type})
// into "field › name: message" strings so users can actually fix them,
// instead of the "[object Object]" that `String(detail)` produces.
// -----------------------------------------------------------------------

import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { isSubscriptionErr } from '../components/admin/billing/shared';

export const formatApiError = (err) => {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) {
    const parts = d
      .map((x) => {
        if (!x || typeof x !== 'object') return String(x || '');
        const field = Array.isArray(x.loc)
          ? x.loc.filter((k) => k !== 'body').join(' › ')
          : '';
        return field ? `${field}: ${x.msg || 'invalid'}` : (x.msg || JSON.stringify(x));
      })
      .filter(Boolean);
    if (parts.length) return parts.join('  •  ');
  }
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') return d.message || JSON.stringify(d);
  return err?.message || 'Something went wrong. Please try again.';
};

export function useApiFeedback() {
  const [error, setError] = useState('');
  const bannerRef = useRef(null);

  const showError = useCallback((errOrMsg) => {
    // A locked (unpaid) workshop already gets one global toast with a
    // Reactivate action from TenantContext — don't stack a second message or an
    // inline banner on top of it.
    if (isSubscriptionErr(errOrMsg)) {
      setError('');
      return '';
    }
    const msg = typeof errOrMsg === 'string' ? errOrMsg : formatApiError(errOrMsg);
    setError(msg);
    toast.error(msg, { duration: 6000 });
    requestAnimationFrame(() => {
      bannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return msg;
  }, []);

  const showSuccess = useCallback((msg) => {
    toast.success(msg);
  }, []);

  const clearError = useCallback(() => setError(''), []);

  return { error, showError, showSuccess, clearError, bannerRef, setError };
}
